import * as actions_admin from 'actions/admin-actions';
import * as actions_attachments from 'actions/attachments-actions';
import * as actions_users from 'actions/users-actions';
import * as actions_work_orders from 'actions/work-orders-actions';
import * as toolbox from 'components/common/toolbox';
import React, { useEffect, useState } from 'react';
import firebase from 'firebase/compat/app';
import { APPS, LOG, ACTION, ROLES, PERMISSIONS, SERVICE_ITEMS, SERVICE_REQUEST_TERMS } from 'components/constants';
import { FormBuilder, ValidateForm, ModalAlert, ModalForm } from 'enspire-manager-framework';
import { navMultipleUrls, numPanels } from 'components/common/toolbox';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { work_order_fields, work_order_form_layout } from 'components/work-orders/work-order/work-order-form/work-order-form-layout';

const moment = require('moment');
const _ = require('lodash');

const WorkOrderForm = (props) => {
	
	/* Hooks --------------------------*/
	
	const seg = 7;
	const history = useHistory();
	const params = useParams();
	const dispatch = useDispatch();
	const routeLocation = useLocation();

	const departments = useSelector((store) => store.settings.departments);
	const employees = useSelector((store) => store.employees.employees);
	const service_request = useSelector((store) => store.serviceRequests.service_request);
	const settings = useSelector((store) => store.settings.settings);
	const user = useSelector(store => store.users?.user);
	const user_permissions = useSelector(store => store.users?.user_permissions);
	const workOrders = useSelector((store) => store.workOrders);
	const work_order = useSelector((store) => store.workOrders.work_order);

	const [formError, setFormError] = useState(null);
	const [original, setOriginal] = useState({});
	const [workOrder, setWorkOrder] = useState();
	const [multiDays, setMultiDays] = useState(false);
	
    const isAssigned = (work_order?.technicianId == user?.profileId);
    const isOwned = (work_order?.ownerId == user?.email);
    const canEdit = user_permissions[PERMISSIONS.WORK_ORDERS_EDIT.key] || isOwned || (isAssigned && user_permissions[PERMISSIONS.WORK_ORDERS_ASSIGNED_EDIT.key]);
    const canDelete = user_permissions[PERMISSIONS.WORK_ORDERS_DELETE.key] || isOwned;

	const existing = (params.work_order_id > 0);
    const isAsset = (params.appId == APPS.ASSETS.id);

	useEffect(() => {
		if (!parseInt(params.work_order_id)) setWorkOrder({
			...work_order_fields(settings?.work_orders?.default_duration ?? 2, service_request?.offsite ?? false),
			description: service_request.description,
		});
	}, [service_request]);

	useEffect(() => {
		if (parseInt(params.work_order_id)) populateForm();
	}, [work_order]);

	useEffect(() => {
		if (props.copyWorkOrder) dispatch(actions_work_orders.subWorkOrderById(params.handle, params.work_order_id));
		return () => {
			var unsubscribe = workOrders.work_order_unsubscribe;
			if (typeof unsubscribe === 'function') unsubscribe();
		};
	}, [params.work_order_id]);

	/* Handlers --------------------------*/

	const handleChange = (e) => {
		let form_error = _.filter(formError, (o) => { return o.field !== e.target.name; });
		setFormError(form_error);
		setWorkOrder(prevWorkOrder => ({ ...prevWorkOrder, [e.target.name]: e.target.value }));
	};
	const handleCheckbox = (field) => {
		setWorkOrder(prevWorkOrder => ({ ...prevWorkOrder, [field]: !workOrder[field] }));
	};
	const handleDate = (field, formDate) => {
		var form_error = _.filter(formError, (o) => { return o.field !== field; });
		setFormError(form_error);

		if (field == 'startDate') {
			let startDate = formDate;
			let endDate = workOrder.endDate;
			if (!multiDays) {
				endDate = moment(startDate).add(workOrder?.duration, 'hours').toDate();
				setWorkOrder(prev => ({ ...prev, startDate: startDate, endDate: endDate }));
			} else {
				setWorkOrder(prev => ({ ...prev, startDate: startDate }));
			}
		}
		if (field == 'endDate') {
			let endDate = formDate;
			setWorkOrder(prev => ({ ...prev, endDate: endDate }));
		}
	};
	const handleCleardate = (field) => {
		setWorkOrder({ ...workOrder, [field]: null });
	}
	const handleUpload = (field, imageUrl) => {
		setWorkOrder({ ...workOrder, imageUrl });
	};
	
	/* Actions --------------------------*/
	
	const populateForm = async () => {
		let startDate = work_order?.startDate?.toDate() ?? null;
		let endDate = work_order?.endDate?.toDate() ?? null;
		let myWorkOrder = { ...work_order,
			startDate: startDate,
			endDate: endDate,
			duration: (moment(startDate).isSame(moment(endDate), 'day'))
				?	moment(work_order?.endDate?.toDate()).diff(moment(work_order?.startDate?.toDate()), 'minutes')
				:	parseFloat(settings?.work_orders?.default_duration ?? '2') * 60,
			technicianId: (props.copyWorkOrder) ? null : work_order?.technicianId,
		};
		setWorkOrder(myWorkOrder);
		setMultiDays(!moment(startDate).isSame(moment(endDate), 'day'));
		setOriginal(myWorkOrder);
	};
	const confirmDelete = () => {
		ModalAlert({
			title: 'Are you sure?',
			text: "This Work Order will be Archived and not appear in work_order search! It can be restored or deleted permanently by visiting the Archive page.",
			icon: 'warning',
			show_cancel: true,
			confirm_color: '#8FBC8B',
			confirm_text: 'Yes, archive it!',
			callback_success: () => deleteWorkOrder(),
		});
	};
	const deleteWorkOrder = () => {
		dispatch(actions_work_orders.updateWorkOrder(params.handle, params.work_order_id, { ...workOrder, deleted: true }, () => { 
			dispatch(actions_admin.saveActivity(params.handle, LOG.WORK_ORDER.key, ACTION.ARCHIVED.key, params.request_id, workOrder.description ));
			history.go(-2);
		}));
	};
	const submitForm = () => {
		var form_error = ValidateForm(workOrder, form_layout);
		setFormError(form_error);

		let newWorkOrder = { ...workOrder,
			endDate: (multiDays) ? workOrder.endDate : moment(workOrder.startDate).add(workOrder.duration, 'minutes').toDate(),
		};
		if (isAsset && !existing) newWorkOrder = { ...newWorkOrder,
			assetTypeId: service_request.assetTypeId,
			assetId: service_request.assetId,
			customerId: service_request.customerId,
		};
		delete newWorkOrder.duration;

		let technician = _.find(employees, { id: newWorkOrder.technicianId });
		let price = (technician?.rateBill) ? technician.rateBill : (settings?.invoices?.defaultLaborRate) ? settings?.invoices?.defaultLaborRate : 0;

		if (!form_error?.length) {
			if (!existing || props.copyWorkOrder) {
				dispatch(actions_work_orders.saveNewWorkOrder(params.handle, params.request_id, newWorkOrder, user, (workOrderId) => {
					dispatch(actions_admin.saveActivity(params.handle, LOG.WORK_ORDER.key, ACTION.CREATED.key, workOrderId, newWorkOrder.description, newWorkOrder ));

					if (service_request?.serviceRequestTermsId == SERVICE_REQUEST_TERMS.SERVICE_CALL.id) {
						history.go(-2);
						setTimeout(() => {
							navMultipleUrls({ actions_users, dispatch, history, numPanels, params, user, urlArray: [
								toolbox.modifyPath( routeLocation?.pathname, 5, `form/0/service_requests/${params.request_id}/work_orders`, 9),
								toolbox.modifyPath( routeLocation?.pathname, 5, `form/0/service_requests/${params.request_id}/work_orders/${workOrderId}/items`, 11),
							]});
						}, 250);
					} else {
						history.goBack();
						let location = toolbox.modifyPath(routeLocation.pathname, seg-2, 'form/0', seg+8);
						location = toolbox.modifyPath(location, seg+3, workOrderId + '/items', seg+7);
						setTimeout(() => { history.push(location); }, 250);
					}				

				}, { lineItems: [{ 
					id: "labor",
					type: SERVICE_ITEMS.LABOR.id,
					name: 'Labor',
					count: 1,
					option: 0,
					price: price,
					calculatedPrice: (settings.invoices?.defaultLaborRate) ? parseFloat(settings?.invoices?.defaultLaborRate) : 0,
				}]}));

			} else {
				dispatch(actions_work_orders.updateWorkOrder(params.handle, params.work_order_id, newWorkOrder, () => { 
					dispatch(actions_admin.saveActivity(params.handle, LOG.WORK_ORDER.key, ACTION.MODIFIED.key, workOrder.id, newWorkOrder.description, newWorkOrder, original ));
					history.goBack(); 
				}));
			}
		};
	};

	const multiDayButton = <>
		<label className="invisible">Span</label>
		<button className={ `btn btn-sm btn-${(multiDays)?'primary':'default'} mb-2`} onClick={() => setMultiDays(!multiDays)}>
			<i className="fa-solid fa-left-right fa-2x"></i>
		</button>
	</>


	const technicians = _.filter(employees, (o) => { return o.roles?.includes(ROLES.TECHNICIAN.id) || o.roles?.includes(ROLES.LEAD.id) });
	const form_layout = work_order_form_layout({
		appId: params.appId,
		departments,
		multiDayButton,
		multiDays,
		technicians,
	});

	return (
		<ModalForm {...props}
			style={1}
			size={'lg'}
			history={history}
			modal_header={`${(!existing)?'New':'Edit'} Work Order`}
			cancel_button_title="Cancel"
			save_button_title={'Save Work Order'}
			submitFormHandler={() => submitForm()}
			confirm_button_disabled={ !canEdit }
			delete_button_title={'Archive'}
			delete_button_callback={(!existing) ? null : () => confirmDelete()}
			delete_button_disabled={ !canDelete }
			show_spinner={workOrders?.work_order_save_pending}
		>
			{workOrder &&
				<FormBuilder
					callbacks={{
						text: handleChange,
						select: handleChange,
						dropzone: handleUpload,
						date: handleDate,
						checkbox: handleCheckbox,
						clear: handleCleardate,
					}}
					form_error={formError}
					form={form_layout}
					record={workOrder}
				/>
			}
		</ModalForm>
	);
};

export default WorkOrderForm;